// The lifecycle paint channel: windows (scrub-independent, from the projection)
// + a scrub point (from the store) → a per-node state map.
//
// Shaped exactly like `useNodeColoring` — a paint context the board reads to
// recolour nodes WITHOUT touching layout. `useBoardLayout`'s input key hashes
// module identity, fields, aspects and the view plan; the lifecycle state map is
// deliberately absent from it, so scrubbing repaints and never re-solves.

import { createContext, useContext, useMemo } from "react";
import {
  scrubOrdinal,
  type LifecycleWindow,
  type Timeline,
  EMPTY_TIMELINE,
} from "@archlang/engine";
import {
  resolveLifecyclePaint as resolvePaintAt,
  resolveLifecycleDeltaAt,
  type LifecycleDelta,
  type LifecyclePaintMap,
} from "@archlang/viewer-core";
import { useComparePoint, useScrubPoint, type ScrubSelection } from "./lifecycleScrubStore.js";

export type { LifecycleDelta, LifecyclePaintMap };

export const LifecycleContext = createContext<LifecyclePaintMap | null>(null);

export function useLifecyclePaint(): LifecyclePaintMap | null {
  return useContext(LifecycleContext);
}

/**
 * Resolve paint for every windowed node at `at`.
 *
 * With the scrubber parked (`at === null`) this is UNFILTERED AUTHORING MODE:
 * the whole roadmap is visible with planned ghosted and deprecated greyed, so
 * an author always sees what they are editing. The badge is what makes a
 * scheduled sunset legible with the scrubber parked — it is the only lifecycle
 * signal that does not depend on a scrub position.
 */
export function resolveLifecyclePaint(
  windows: ReadonlyMap<string, LifecycleWindow>,
  at: ScrubSelection,
  timeline?: Timeline,
): LifecyclePaintMap {
  // The stored point is a NAME; its ordinal is resolved here, against whatever
  // the axis looks like right now. That is what keeps the painted board and the
  // scrubber's own label agreeing after an edit reshapes the axis — and it is
  // the only part of this that belongs to the UI. Everything downstream of the
  // ordinal is shared with the headless renderer, so the two cannot drift.
  return resolvePaintAt(windows, ordinalFor(at, timeline), timeline);
}

/** The delta between two SCRUB POINTS — the names the handles hold — resolved
 *  against the current axis and handed to the shared classifier. */
export function resolveLifecycleDelta(
  windows: ReadonlyMap<string, LifecycleWindow>,
  from: ScrubSelection,
  to: ScrubSelection,
  timeline?: Timeline,
): LifecycleDelta {
  if (to === null) return new Map();
  // A parked left handle reads as `now` — comparing "everything" against a point
  // has no meaning, but comparing today against a future marker does, and that
  // is what a reader means by dropping a second handle while parked.
  return resolveLifecycleDeltaAt(windows, ordinalFor(from, timeline), ordinalFor(to, timeline));
}

/** Hook form — memoised on (windows, timeline, point) only. */
export function useResolvedLifecyclePaint(
  windows: ReadonlyMap<string, LifecycleWindow> | undefined,
  timeline?: Timeline,
): LifecyclePaintMap {
  const at = useScrubPoint();
  return useMemo(
    () => resolveLifecyclePaint(windows ?? new Map(), at, timeline),
    [windows, at, timeline],
  );
}

export function useLifecycleDelta(
  windows: ReadonlyMap<string, LifecycleWindow> | undefined,
  timeline?: Timeline,
): LifecycleDelta {
  const at = useScrubPoint();
  const compare = useComparePoint();
  return useMemo(
    () => resolveLifecycleDelta(windows ?? new Map(), at, compare, timeline),
    [windows, at, compare, timeline],
  );
}

/** A stored point resolved against the CURRENT axis; parked reads as `now`. */
function ordinalFor(at: ScrubSelection, timeline: Timeline | undefined): number {
  const tl = timeline ?? EMPTY_TIMELINE;
  return at === null ? tl.now : scrubOrdinal(tl, at);
}
